import React, { useEffect, useState } from 'react';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import { HomeOutlined, EditOutlined, DeleteFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Pagination,
  Popconfirm,
  ConfigProvider,
  BackTop,
} from 'antd';
import { Table, Space, Tooltip, Button } from 'antd';
import TableDateRow from 'modules/layoutModule/components/TableDateRow/TableDateRow';
import {
  getNewsRequest,
  removeSpecificNewsRequest,
} from 'modules/newsModule/requests/newsRequests';
import plPL from 'antd/lib/locale/pl_PL';
import moment from 'moment';
import TableDataRowTypes from 'modules/layoutModule/enums/TableDataRowTypes';
import useColumnSearch from 'modules/layoutModule/hooks/useColumnSearch';

export interface NewsTableTypes {
  id: number;
  dateAdd: moment.MomentInput;
  name: string;
  isActive: number;
}

const News = (): JSX.Element => {
  const [tableData, setTableData] = useState<NewsTableTypes[]>([]);
  const [loading, toggleLoading] = useState<boolean>(true);
  const [searchedColumn, setSearchedColumn] = useState('');

  const confirmDelete = (id: number) => {
    removeSpecificNewsRequest(id).then(() => {
      downloadNews();
    });
  };

  const downloadNews = () =>
    getNewsRequest().then((response) => {
      if (response) {
        const { data } = response;
        setTableData(data);
        toggleLoading(false);
      } else {
        toggleLoading(false);
      }
    });

  useEffect(() => {
    if (tableData.length === 0) {
      downloadNews();
    }
  }, [tableData]);

  const columns = [
    {
      title: 'Data',
      dataIndex: 'dateAdd',
      key: 'dateAdd',
      width: 70,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.DATE_ADD} />
      ),
      sorter: (
        a: { dateAdd: moment.MomentInput },
        b: { dateAdd: moment.MomentInput }
      ) => moment(a.dateAdd).unix() - moment(b.dateAdd).unix(),
    },
    {
      title: 'Tytuł',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
      ...useColumnSearch(
        'name',
        'Szukaj po tytule',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn
      ),
      render: (text: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.NAME_LINK}
          link={`aktualnosci/newsy/${record.categoryUrl}/${record.url}`}
        />
      ),
    },
    {
      title: 'Okładka',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.IMAGE} />
      ),
    },
    {
      title: 'Kategoria',
      dataIndex: 'idCategory',
      width: 160,
      sorter: (a: { idCategory: number }, b: { idCategory: number }) =>
        a.idCategory - b.idCategory,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.CATEGORY_TAG} />
      ),
    },
    {
      title: 'Licznik',
      dataIndex: 'viewCount',
      width: 100,
      sorter: (a: { viewCount: number }, b: { viewCount: number }) =>
        a.viewCount - b.viewCount,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.VIEWS} />
      ),
    },
    {
      title: 'Aktywna',
      dataIndex: 'isActive',
      key: 'isActive',
      width: '150px',
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.IS_ACTIVE} />
      ),
      sorter: (a: { isActive: number }, b: { isActive: number }) =>
        a.isActive - b.isActive,
      filters: [
        {
          text: 'TAK',
          value: 'isActive',
        },
        {
          text: 'NIE',
          value: 'isNotActive',
        },
      ],
      onFilter: (value: string, record: { isActive: boolean }) => {
        if (value === 'isActive') {
          return record.isActive;
        }
        return !record.isActive;
      },
    },
    {
      title: 'Akcje',
      dataIndex: 'operation',
      key: 'operation',
      width: 100,
      render: (text: string, item: NewsTableTypes) => (
        <Space
          size="middle"
          style={{ display: 'Flex', flexDirection: 'column' }}
        >
          <Tooltip title="Edytuj wpis">
            <Link to={`/news/${item.id}`}>
              <Button type="primary" shape="circle" icon={<EditOutlined />} />
            </Link>
          </Tooltip>
          <Tooltip title="Usuń wpis">
            <Popconfirm
              title="Czy na pewno chcesz usunąc ten wpis?"
              placement="left"
              onConfirm={() => confirmDelete(item.id)}
              okText="Tak"
              cancelText="Nie"
            >
              <Button type="link" disabled={item.isActive === 1}>
                <DeleteFilled />
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Aktualności</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/news">Lista newsów</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <Table
          // @ts-ignore
          columns={columns}
          // @ts-ignore
          dataSource={tableData}
          loading={loading}
          rowKey={(record) => record.id}
        >
          <Pagination />
        </Table>
        <BackTop />
      </ConfigProvider>
    </>
  );
};

export default News;
